import { CommonModule } from '@angular/common';
import { Component, OnInit, Input, Output, EventEmitter, inject } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ColorUtilsService } from '@app/core/services/color-utils.service';
import { TranslatePipe } from '@ngx-translate/core';

const hexColorRegex = /^#?([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/i;

@Component({
  selector: 'topo-color-picker',
  imports: [CommonModule, FlexLayoutModule, TranslatePipe],
  templateUrl: './color-picker.component.html',
  styleUrls: ['./color-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: ColorPickerComponent
    }
  ]
})
export class ColorPickerComponent implements OnInit, ControlValueAccessor {
  @Input() set selectedColor(value: string) {
    if (value) {
      this._selectedColor = value.toString();
    }
  }
  get selectedColor(): string {
    return this._selectedColor ?? '';
  }

  private colorUtils = inject(ColorUtilsService);

  @Input() disabled = false;
  @Input() selectedColorWidth = 100;
  _selectedColor: string;
  @Output() selectedColorChanged = new EventEmitter<string>();
  selectedColorMissing = 'var(--neutral-color-400)';
  touched = false;
  private _displayAs: 'popout' | 'inline-narrow' | 'inline-wide' = 'inline-wide';
  @Input()
  public get displayAs(): 'popout' | 'inline-narrow' | 'inline-wide' {
    return this._displayAs;
  }
  public set displayAs(value: 'popout' | 'inline-narrow' | 'inline-wide') {
    this.isInline = value != 'popout';
    if (this.isWide !== (value === 'inline-wide')) {
      this.setColorColumns(value === 'inline-wide');
    }
    this.isWide = value === 'inline-wide';
    this._displayAs = value;
    if (!this.isWide) {
    }
  }

  isInline = true;
  isWide = true;

  colorColumns: any[][] = this.colorColumnsToSquareColumns([
    ['#212121', '#616161', '#9E9E9E', '#FEFEFE'],
    ['#263238', '#455A64', '#607D8B', '#90A4AE'],
    ['#B71C1C', '#D32F2F', '#F44336', '#E57373'],
    ['#E65100', '#F57C00', '#FF9800', '#FFB74D'],
    ['#1B5E20', '#388E3C', '#4CAF50', '#81C784'],
    ['#004D40', '#00796B', '#009688', '#4DB6AC'],
    ['#01579B', '#0288D1', '#03A9F4', '#4FC3F7'],
    ['#0D47A1', '#1976D2', '#2196F3', '#64B5F6'],
    ['#1A237E', '#303F9F', '#3F51B5', '#7986CB'],
    ['#4A148C', '#7B1FA2', '#9C27B0', '#BA68C8'],
    ['#880E4F', '#C2185B', '#E91E63', '#F06292']
  ]);

  setColorColumns(isWideView = false) {
    if (!isWideView) {
      this.colorColumns = this.colorColumnsToSquareColumns([
        ['#212121', '#616161', '#9E9E9E', '#FEFEFE'],
        ['#B71C1C', '#D32F2F', '#F44336', '#E57373'],
        ['#E65100', '#F57C00', '#FF9800', '#FFB74D'],
        ['#1B5E20', '#388E3C', '#4CAF50', '#81C784'],
        ['#004D40', '#00796B', '#009688', '#4DB6AC'],
        ['#01579B', '#0288D1', '#03A9F4', '#4FC3F7'],
        ['#0D47A1', '#1976D2', '#2196F3', '#64B5F6'],
        ['#1A237E', '#303F9F', '#3F51B5', '#7986CB'],
        ['#4A148C', '#7B1FA2', '#9C27B0', '#BA68C8'],
        ['#880E4F', '#C2185B', '#E91E63', '#F06292']
      ]);
      return;
    }

    this.colorColumns = this.colorColumnsToSquareColumns([
      ['#212121', '#616161', '#9E9E9E', '#FEFEFE'],
      // ['#212121', '#616161', '#9E9E9E', '#E0E0E0'], less white
      ['#263238', '#455A64', '#607D8B', '#90A4AE'],
      ['#B71C1C', '#D32F2F', '#F44336', '#E57373'],
      ['#E65100', '#F57C00', '#FF9800', '#FFB74D'],
      ['#1B5E20', '#388E3C', '#4CAF50', '#81C784'],
      ['#004D40', '#00796B', '#009688', '#4DB6AC'],
      ['#01579B', '#0288D1', '#03A9F4', '#4FC3F7'],
      ['#0D47A1', '#1976D2', '#2196F3', '#64B5F6'],
      ['#1A237E', '#303F9F', '#3F51B5', '#7986CB'],
      ['#4A148C', '#7B1FA2', '#9C27B0', '#BA68C8'],
      ['#880E4F', '#C2185B', '#E91E63', '#F06292']
    ]);
  }

  hasError = false;

  onChange = value => {};
  onTouched = () => {};

  ngOnInit(): void {}

  selectColor(colorCode: string): void {
    const isValid = !!colorCode ? hexColorRegex.test(colorCode) : true;

    this.hasError = !isValid;

    if (this.disabled || !isValid) {
      return;
    }

    if (this.selectedColor && colorCode === this.selectedColor) {
      colorCode = null;
    }
    this.selectedColor = colorCode;

    this.selectedColorChanged.emit(this.selectedColor);

    this.markAsTouched();
    this.onChange(this.selectedColor);
  }

  writeValue(colorCode: string) {
    this.selectedColor = colorCode;
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  onSelectedColorChanged($event) {
    this.selectColor($event.srcElement.value);
  }

  colorColumnsToSquareColumns(colors: string[][]): { color: string; lightOrDark: string }[][] {
    return colors.map(column =>
      column.map(color => {
        return {
          color,
          lightOrDark: this.colorUtils.isColorLightOrDark(color, 200)
        };
      })
    );
  }
}
